import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { countryCodes } from '@/constants/countryCodes';
import { setMapboxPlace } from '@/redux/modules/mapboxPlace';
import apiRequest from '@/services/apiRequest';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import {
  IMapboxPlacesResponse,
  IMapboxSuggestionsResponse,
} from '@/services/types/mapbox/mapboxApi';
import { getCookie, setCookie } from '@/utility/cookie';

import useDebounce from './useDebounce';
import { useGeoDataMyIp } from './useGeoDataMyIp';
import usePrevious from './usePrevious';

export const MAPBOX_BASE_URL = 'https://api.mapbox.com';
export const MAPBOX_TOKEN = process.env.mapboxApiToken || 'xxx';
const OUTDOORSY_MAPBOX_SESSION_TOKEN_COOKIE = 'outdoorsy-mapbox-session-token';

export type TMapboxQuery = {
  text: string;
  location?: Array<number>;
  types?: string;
  limit?: number;
  // whether to perform a reverse geocoding lookup (from lng/lat to place name)
  isReverseLookup?: boolean;
};

const genMapboxSessionToken = () => {
  const sessionToken = uuid();
  const expireInSixtyMinutes = 1 / 24;
  setCookie(OUTDOORSY_MAPBOX_SESSION_TOKEN_COOKIE, sessionToken, {
    expires: expireInSixtyMinutes,
  });
  return sessionToken;
};

export const getMapboxSessionToken = (forceResetCookie?: boolean) => {
  // attempt to get the session token from the cookie
  const sessionCookieToken = getCookie(OUTDOORSY_MAPBOX_SESSION_TOKEN_COOKIE);

  if (!sessionCookieToken) {
    // if the cookie doesn't exist or has expired
    // generate a new token and set a new cookie to expire in 60 minuttes (per mapbox documentation)
    const sessionToken = genMapboxSessionToken();
    return sessionToken;
  }

  if (forceResetCookie) {
    // the mapbox documentations states that on retrieve we need to terminate the current session
    // generate a new token, but use the previous token from the cookie for the retrieve request
    genMapboxSessionToken();
  }

  return sessionCookieToken;
};

export const genRetrieveMapboxUrl = (mapboxId: string): string => {
  if (!mapboxId) {
    return '';
  }

  const sessionToken = getMapboxSessionToken(true);
  const url = new URL(`${MAPBOX_BASE_URL}/search/searchbox/v1/retrieve/${mapboxId}`);
  url.search = new URLSearchParams({
    session_token: sessionToken,
    access_token: MAPBOX_TOKEN,
  }).toString();
  return url.toString();
};

export const genMapboxUrl = (query: TMapboxQuery, upgradeMapboxApiExperiment?: boolean): string => {
  const { location, isReverseLookup } = query;

  // we either perform reverse geocoding lookup by lng/lat or a geocoding lookup by place name
  const searchText =
    isReverseLookup && location ? encodeURI(location.join(',')) : encodeURI(query.text);
  let url = new URL(`${MAPBOX_BASE_URL}/geocoding/v5/mapbox.places/${searchText}.json`);

  let params = {};
  if (upgradeMapboxApiExperiment) {
    const sessionToken = getMapboxSessionToken();
    url = new URL(`${MAPBOX_BASE_URL}/search/searchbox/v1/suggest`);
    params = {
      q: searchText,
      session_token: sessionToken,
      access_token: MAPBOX_TOKEN,
      country: countryCodes.join(','),
      ...(location ? { proximity: location.join(',') } : {}),
      types: 'place,locality,neighborhood,address,poi',
      poi_category:
        'outdoors,airport,park,tourist_attraction,river,monument,lake,mountain,forest,hostel,island,canal,nature_reserve,rest_area,campground,fair_grounds,beach,casino,marina,viewpoint,pier,picnic_shelter,sports_center,vineyard,racetrack,climbing,waterfall,fishing,resort,hunting_area,water_park,cave,town,trailhead,surf_spot,mountain_hut,boat_launch,rafting_spot,city,bed_and_breakfast,theme_park',
      limit: String(query.limit || 6),
    };
  } else {
    params = {
      access_token: MAPBOX_TOKEN,
      country: countryCodes.join(','),
      ...(location ? { proximity: location.join(',') } : {}),
      types: query.types || 'region,place,district,locality,poi',
      limit: String(query.limit || 6),
    };
  }

  url.search = new URLSearchParams(params).toString();
  return url.toString();
};

function useMapboxQuery(query?: TMapboxQuery | null, delay?: number) {
  const { data: geoDataMyIp } = useGeoDataMyIp(!!query?.location);
  const location = geoDataMyIp ? [geoDataMyIp.lng, geoDataMyIp.lat] : undefined;
  const upgradeMapboxApiExperiment = useExperimentIsEnabled(OptimizelyFlags.UPGRADE_MAPBOX_API);
  const url = query
    ? genMapboxUrl({ ...query, location: query?.location ?? location }, upgradeMapboxApiExperiment)
    : '';
  const debouncedUrl = useDebounce<typeof url>(url, delay);
  const previousUrl = usePrevious(debouncedUrl);
  const [results, setResults] = useState<
    IMapboxPlacesResponse['features'] | IMapboxSuggestionsResponse['suggestions']
  >([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (debouncedUrl === previousUrl) return;
    if (!debouncedUrl) {
      setResults([]);
      dispatch(setMapboxPlace(null));
      return;
    }
    let isCanceled = false;
    const fetchPlaces = async () => {
      const response = await apiRequest<IMapboxPlacesResponse | IMapboxSuggestionsResponse>({
        url: debouncedUrl,
      });
      if (response && !isCanceled) {
        if (upgradeMapboxApiExperiment) {
          const suggestions = (response as IMapboxSuggestionsResponse).suggestions;
          setResults(suggestions);
          dispatch(setMapboxPlace(suggestions?.[0] ?? null));
        } else {
          const features = (response as IMapboxPlacesResponse).features;
          setResults(features);
          dispatch(setMapboxPlace(features?.[0] ?? null));
        }
      }
    };
    fetchPlaces();
    return () => {
      isCanceled = true;
    };
  }, [debouncedUrl, dispatch, previousUrl, upgradeMapboxApiExperiment]);

  return results;
}

export default useMapboxQuery;
